import React, { useEffect, useState } from "react";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import { getAllThesisGrades } from "../../services/feedbackService";
import axiosInstance from "../../api/axios";
import {
  Box,
  Tooltip,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/Info";
import "./datatable.scss";

export const StudentGrades = () => {
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedInfo, setExpandedInfo] = useState({ rowId: null, examiner: null });

  // --- Modal editor state ---
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorRow, setEditorRow] = useState(null);
  const [editorField, setEditorField] = useState(null);
  const [editorValue, setEditorValue] = useState("");
  const apiRef = useGridApiRef();

  const THESIS_STATUS_LABELS = {
    REPORT_REVISION_REQUIRED: "Viva Cleared, Revise Report",
    RESUBMISSION: "Viva Not Cleared",
    PHASE_COMPLETED: "Viva Cleared",
  };

  const THESIS_STATUS_OPTIONS = [
    { value: "REPORT_REVISION_REQUIRED", label: "Viva Cleared, Revise Report" },
    { value: "RESUBMISSION", label: "Viva Not Cleared" },
    { value: "PHASE_COMPLETED", label: "Viva Cleared" },
  ];

  const EXAMINERS = [
    { id: 1, label: "Examiner 1" },
    { id: 2, label: "Examiner 2" },
    { id: 3, label: "Reviewer" }
  ];


  // --- Fetch data ---
  const fetchThesisGrades = async () => {
    try {
      const data = await getAllThesisGrades();
      setGrades(data.data);
    //  console.log("Fetched thesis grades:", data.data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThesisGrades();
  }, []);

  // --- Toggle expanded feedback ---
  const toggleRowExpansion = (rowId, examiner) => {
   // console.log("Toggling row expansion:", rowId, examiner, expandedInfo);
    if (expandedInfo.rowId === rowId && expandedInfo.examiner === examiner) {
      setExpandedInfo({ rowId: null, examiner: null });
    } else {
      setExpandedInfo({ rowId, examiner });
    }
  };

  // --- Open / close modal editors ---
  const openEditor = (row, field) => {
    setEditorRow(row);
    setEditorField(field);
    setEditorValue(row[field] ?? "");
    setEditorOpen(true);
  };
  const closeEditor = () => {
    setEditorOpen(false);
    setEditorRow(null);
    setEditorField(null);
    setEditorValue("");
  };

  // --- Save edited value ---
  const saveEditor = async () => {
  if (!editorRow || !editorField) return;

  const id = editorRow.thesisStatusId;
  const thesisFeedbackId1 = editorRow.thesisFeedbackId1;
  const thesisFeedbackId2 = editorRow.thesisFeedbackId2;
  
  //console.log(editorRow);
  const studentId = editorRow.studentId;

  // helper: convert empty string -> null
  const normalize = (v) =>
    v === undefined || v === null
      ? null
      : typeof v === "string" && v.trim() === ""
      ? null
      : v;

  // build payload using current editorValue for the edited field
  const payload = {
    thesisStatusId: id,
    studentId: studentId,
    finalMarks:
      editorField === "finalMarks"
        ? normalize(editorValue)
        : normalize(editorRow.finalMarks),
    finalGrade:
      editorField === "finalGrade"
        ? normalize(editorValue)
        : normalize(editorRow.finalGrade),
    finalFeedback:
      editorField === "finalFeedback"
        ? normalize(editorValue)
        : normalize(editorRow.finalFeedback),
    thesisVivaStatus:
      editorField === "thesisVivaStatus"
        ? normalize(editorValue)
        : normalize(editorRow.thesisVivaStatus),
  };

 // console.log("Outgoing payload →", JSON.stringify(payload, null, 2));
 // console.log("Axios instance:", axiosInstance.defaults.transformRequest);


  try {
    const res = await axiosInstance.put(
      "/updateFinalGradeMarksFeedbackStatus",
      payload,
      { headers: { "Content-Type": "application/json" },
      transformRequest: [(data) => JSON.stringify(data)], }
    );

    // merge server result into grid
    setGrades((prev) =>
      prev.map((g) =>
        g.thesisStatusId === id ? { ...g, ...payload } : g
      )
    );

    closeEditor();
  } catch (err) {
    console.error("Save failed:", err);
    alert("Save failed");
  }
};



  // --- Share feedback button ---
  const shareFeedback = async (row) => {
    try {
      const { thesisStatusId, thesisFeedbackId1, thesisFeedbackId2 } = row;
      await axiosInstance.put(`/shareThesisGrade?id=${thesisStatusId}`);
      if (thesisFeedbackId1) {
       await axiosInstance.put(`/shareThesisFeedback?id=${thesisFeedbackId1}`);
      }
      if(thesisFeedbackId2) {
       await axiosInstance.put(`/shareThesisFeedback?id=${thesisFeedbackId2}`);
      }
      alert("Grade shared successfully!");
      setGrades((prev) =>
        prev.map((g) =>
          g.thesisStatusId === thesisStatusId
            ? { ...g, share_to_student: true }
            : g
        )
      );
    } catch (err) {
      console.error("Error sharing grade:", err);
      alert("Failed to share grade.");
    }
  };

  // --- Columns ---
  const columns = [
    { field: "studentName", headerName: "Student", width: 100 },
    { field: "enrollmentNumber", headerName: "Enrollment No", width: 130 },
    { field: "reportType", headerName: "Type", width: 100 },
    { field: "termType", headerName: "Submission Type", width: 120,
      renderHeader: () => (
        <div style={{ textAlign: "center", fontWeight: 600 }}>
          Submission<br />Type
        </div>
      ),
     },
    { field: "Examiners", headerName: "Examiners", width: 200, 
            renderCell: (params) => {
        const { examinerName1, examinerName2, examinerName3 } = params.row || {};

      //  if (!examinerName1 || !examinerName2 || !examinerName3) {
      //    return <span style={{ color: "#999" }}>Not Available</span>;
      //  }

        return (
          <div style={{ lineHeight: "18px" }}>
            <div><strong>Examiner 1:</strong> {examinerName1 ?? "Not Available"}</div>
            <div><strong>Examiner 2:</strong> {examinerName2 ?? "Not Available"}</div>
            <div><strong>Reviewer:</strong> {examinerName3 ?? "Not Available"}</div>
          </div>
        );
      }
     },
          {
        field: "totalMarks",
        headerName: "Total Marks",
        width: 200,
        renderCell: (params) => {
          const row = params.row;
          const isExpanded = expandedInfo.rowId === row.thesisStatusId && 
          expandedInfo.examiner==="TotalMarks";

          return (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <div style={{ lineHeight: "18px" }}>
                <div>
                  <strong>Examiner 1:</strong> {row.totalMarks1 ?? "-"}
                </div>
                <div>
                  <strong>Examiner 2:</strong> {row.totalMarks2 ?? "-"}
                </div>
              </div>

              <button
                onClick={() => toggleRowExpansion(row.thesisStatusId, "TotalMarks")}
                style={{
                  backgroundColor: isExpanded ? "#d32f2f" : "#1976d2",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  padding: "4px 8px",
                  cursor: "pointer",
                  fontSize: "0.8rem",
                }}
              >
                {isExpanded ? "Hide" : "Show"}
              </button>
            </Box>
          );
        },
      },
      { field: 'gradeOverallByExaminer', headerName: 'Grade', width: 120,
        renderCell: (params) => {
          const row = params.row;

          return (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <div style={{ lineHeight: "18px" }}>
                <div>
                  <strong>Examiner 1:</strong> {row.gradeOverallByExaminer1 ?? "-"}
                </div>
                <div>
                  <strong>Examiner 2:</strong> {row.gradeOverallByExaminer2 ?? "-"}
                </div>
              </div>
              </Box>
              );
            }
       },
    {
      field: "finalMarks",
      headerName: "Final Marks",
      width: 75,
      renderHeader: () => (
        <div style={{ textAlign: "center", fontWeight: 600 }}>
          Final<br />Marks
        </div>
      ),
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <span>{params.value ?? "-"}</span>
          <Tooltip title="Edit marks">
            <IconButton
              size="small"
              onClick={() => openEditor(params.row, "finalMarks")}
              disabled={params.row.share_to_student === true}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },

    {
      field: "finalGrade",
      headerName: "Final Grade",
      width: 75,
      renderHeader: () => (
        <div style={{ textAlign: "center", fontWeight: 600 }}>
          Final<br />Grade
        </div>
      ),
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <span>{params.value ?? "-"}</span>
          <Tooltip title="Edit grade">
            <IconButton
              size="small"
              onClick={() => openEditor(params.row, "finalGrade")}
              disabled={params.row.share_to_student === true}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },

    {
      field: "finalFeedback",
      headerName: "Final Feedback",
      width: 100,
      renderHeader: () => (
        <div style={{ textAlign: "center", fontWeight: 600 }}>
          Final<br />Feedback
        </div>
      ),
      renderCell: (params) => {
        const row = params.row;
        const isExpanded =
          expandedInfo.rowId === row.thesisStatusId &&
          expandedInfo.examiner === "final";
        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "140px",
              }}
            >
              {row.finalFeedback
                ? row.finalFeedback.length > 20
                  ? row.finalFeedback.slice(0, 20) + "..."
                  : row.finalFeedback
                : "-"}
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <Tooltip title="Edit feedback">
                <IconButton
                  size="small"
                  onClick={() => openEditor(row, "finalFeedback")}
                  disabled={params.row.share_to_student === true}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <button
                onClick={() => toggleRowExpansion(row.thesisStatusId, "final")}
                // disabled={params.row.share_to_student === true}
                style={{
                  backgroundColor: isExpanded ? "#d32f2f" : "#1976d2",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  padding: "3px 8px",
                  cursor: "pointer",
                  fontSize: "0.8rem",
                }}
              >
                {isExpanded ? "Hide" : "Show"}
              </button>
            </div>
          </Box>
        );
      },
    },

    {
      field: "thesisVivaStatus",
      headerName: "Status",
      width: 100,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <span>
            {THESIS_STATUS_LABELS[params.value] ?? params.value ?? "-"}
          </span>
          <Tooltip title="Edit Status">
            <IconButton
              size="small"
              onClick={() => openEditor(params.row, "thesisVivaStatus")}
              disabled={params.row.share_to_student === true}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    }

  ];

  const actionColumn = [
    {
      field: "action",
      headerName: "Action",
      width: 100,
      renderCell: (params) => {
        const { share_to_student } = params.row;
        return (
          <div className="cellAction">
            <button
              onClick={() => shareFeedback(params.row)}
              disabled={share_to_student === true}
            >
              {share_to_student ? "Shared" : "Share"}
            </button>
          </div>
        );
      },
    },
  ];

  if (loading) return <p>Loading…</p>;
  if (error) return <p>Error: {error.message}</p>;

  return (
    <div className="datatable">
      <DataGrid
        apiRef={apiRef}
        rows={grades}
        getRowId={(row) => row.thesisStatusId}
        columns={columns.concat(actionColumn)}
        pageSizeOptions={[5, 10]}
        initialState={{
          pagination: { paginationModel: { page: 0, pageSize: 10 } },
        }}
      />

      <Collapse in={!!expandedInfo.rowId} timeout="auto" unmountOnExit>
        {expandedInfo.rowId && (
          <Box
            sx={{
              mt: 1,
              p: 1,
              backgroundColor: "#fafafa",
              border: "1px solid #ddd",
              borderRadius: "6px",
            }}
          >
            {(() => {
              const row = grades.find(
                (g) => g.thesisStatusId === expandedInfo.rowId
              );
              if (!row) return null;

              if (expandedInfo.examiner === "final") {
                return (
                  <Box
                    sx={{
                      mt: 1,
                      p: 2,
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      backgroundColor: "#fafafa",
                      fontSize: "0.9rem",
                    }}
                  >
                    <strong>Final Feedback</strong>
                    <p style={{ marginTop: 8 }}>
                      {row.finalFeedback ?? "-"}
                    </p>
                  </Box>
                );
              }

            if (expandedInfo.examiner === "TotalMarks") {
              return (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                    xs: "1fr",
                    md: `repeat(${EXAMINERS.length}, minmax(0, 1fr))`,
                  },
                    gap: 1,
                  }}
                >
                 {EXAMINERS.map((ex) => (
              <Box
                key={ex.id}
                sx={{
                  p: 1,
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  backgroundColor: "#fff",
                  fontSize: "0.82rem",
                  lineHeight: 1.4,
                }}
              >
                <strong>{ex.label}</strong>

                <div><b>Report Marks:</b> {row[`reportMarksByExaminer${ex.id}`] ?? 0}</div>
                <div><b>Presentation Marks:</b> {row[`presentationMarksByExaminer${ex.id}`] ?? 0}</div>
                <div><b>Viva Marks:</b> {row[`vivaMarksByExaminer${ex.id}`] ?? 0}</div>
                
                <div style={{ marginTop: 4 }}>
                  <b>Report Feedback:</b>
                  <div style={{ whiteSpace: "normal" }}>
                    {row[`reportFeedbackByExaminer${ex.id}`] ?? "-"}
                  </div>
                  <b>Presentation Feedback:</b>
                  <div style={{ whiteSpace: "normal" }}>
                  {row[`presentationFeedbackByExaminer${ex.id}`] ?? "-"}
                  </div>
                  <b>Other Feedback:</b>
                  <div style={{ whiteSpace: "normal" }}>
                    {row[`otherFeedbackByExaminer${ex.id}`] ?? "-"}
                  </div>
                  <b>Overall Feedback:</b>
                  <div style={{ whiteSpace: "normal" }}>
                    {row[`overallFeedbackByExaminer${ex.id}`] ?? "-"}
                  </div>

                </div>
              </Box>
            ))}
          </Box>
        );
      }
    return null;
            })()}
      </Box>
        )}

      </Collapse>

      {/* ---------- Modal Editor ---------- */}
      <Dialog open={editorOpen} onClose={closeEditor} fullWidth maxWidth="sm">
        <DialogTitle>
          {editorField === "finalGrade"
            ? `Edit Final Grade — ${editorRow?.studentName ?? ""}`
            : editorField === "finalMarks"
            ? `Edit Final Marks — ${editorRow?.studentName ?? ""}`
            : editorField === "finalFeedback"
            ?  `Edit Final Feedback — ${editorRow?.studentName ?? ""}`
            : `Edit Status — ${editorRow?.studentName ?? ""}`}
        </DialogTitle>
        <DialogContent>
          {editorField === "thesisVivaStatus" ? (
            <TextField
              select
              fullWidth
              label="Select Status"
              value={editorValue || ""}
              onChange={(e) => setEditorValue(e.target.value)}
              SelectProps={{ native: true }}
              autoFocus
            >
              <option value="" disabled>Select Status</option>
              {THESIS_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </TextField>
          ):
           editorField === "finalFeedback" ? (
            <TextField
              fullWidth
              multiline
              minRows={6}
              label="Final Feedback (Ctrl+Enter to save)"
              value={editorValue}
              onChange={(e) => setEditorValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.ctrlKey && e.key === "Enter") {
                  e.preventDefault();
                  saveEditor();
                }
              }}
              autoFocus
            />
          ) : (
            <TextField
              fullWidth
              label="Enter value (leave empty to clear)"
              value={editorValue ?? ""}
              onChange={(e) => setEditorValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  saveEditor();
                }
              }}
              autoFocus
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditor}>Cancel</Button>
          <Button variant="contained" onClick={saveEditor}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
